/**
 * <contact-form> — progressively enhances the storefront support form.
 *
 * The markup (in sections/contact-form.liquid) is a normal <form> with native
 * `required` / `type="email"` validation, so the browser handles (and localizes)
 * all field validation. This element only does what native HTML can't:
 *   - serialise a clean English JSON payload and POST it to the ticket API
 *   - toggle the order-number field for reasons that don't need it
 *   - preselect a reason from a ?reason= deep link and flag ?influencer= referrals
 *   - surface the API response in the banner
 */
const HIDDEN_CLASS = 'contact__is-hidden';
const ENDPOINT = 'https://retoure-api.pammys.com/api/storefront/create-ticket';

// Order-number input mask: enforce the "#12" prefix + 7 digits (e.g. #12XXXXXXX).
const ORDER_PREFIX = '#12';

// ?reason= deep-link slugs -> reason codes (hyphen + underscore both accepted).
const REASON_SLUGS = { 'cancel-order': 'cancel_order', cancel_order: 'cancel_order' };

// Reason codes whose selection overrides the submit-button label (code -> i18n key).
const SUBMIT_LABELS = { cancel_order: 'submitWiderruf' };

const INFLUENCER_PLACEHOLDER = '[influencer]';
const INFLUENCER_NAME_MAX_LENGTH = 40;

class ContactForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form || this.dataset.enhanced) return;
    this.dataset.enhanced = 'true';

    const config = this._readConfig();
    this.i18n = config.i18n || {};
    this.localization = config.localization || {};

    this.banner = this.querySelector('[data-response-banner]');
    this.bannerContent = this.querySelector('[data-response-content]');
    this.iconSuccess = this.querySelector('[data-icon-success]');
    this.iconError = this.querySelector('[data-icon-error]');
    this.reasonSelect = this.querySelector('[data-reason-select]');
    this.orderField = this.querySelector('[data-order-number-field]');
    this.advisory = this.querySelector('[data-order-advisory]');
    this.submitButton = this.form.querySelector('[type="submit"]');
    this.defaultSubmitLabel = this.submitButton?.textContent ?? '';

    this._applyInfluencerTitle();

    this.form.addEventListener('submit', this._onSubmit);

    if (this.reasonSelect) {
      this.reasonSelect.addEventListener('change', this._onReasonChange);
      this._applyDeepLinkReason();
      this._onReasonChange();
    }

    if (this.orderField) this._initOrderMask();

    this.querySelector('[data-file-input]')?.addEventListener('change', this._onFilesChange);
  }

  _t(key) {
    return this.i18n[key] || '';
  }

  _readConfig() {
    const script = this.querySelector('[data-contact-form-config]');
    try {
      return script ? JSON.parse(script.textContent) : {};
    } catch {
      return {};
    }
  }

  _onSubmit = async (event) => {
    event.preventDefault();
    if (this.submitting) return;

    this._setBusy(true);
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(this._payload()),
      });
      const payload = await this._parse(response);
      // The backend returns `message` already localized to the shopper's language.
      this._showBanner(response.ok ? 'success' : 'error', payload?.message || this._t('unexpectedServerResponse'));

      if (response.ok) {
        this.form.reset();
        this._clearFileNames();
        this._onReasonChange();
      }
    } catch {
      this._showBanner('error', this._t('networkError'));
    } finally {
      this._setBusy(false);
    }
  };

  _payload() {
    const field = (name) => (this.form.elements[name]?.value ?? '').trim();

    const data = { name: field('name'), email: field('email'), message: field('message') };

    if (this.reasonSelect) data.reason = this.reasonSelect.value;

    if (this.orderField && !this._reasonSkipsOrder()) {
      const order = this.orderField.value.trim();
      if (order) data.order_number = order;
    }

    // Extra Zendesk tags (the backend allowlists them). Future tags just push here.
    const tags = [];
    if (this._hasInfluencerParam()) tags.push('vip');
    if (tags.length) data.tags = tags;

    const { language, country, market } = this.localization;
    if (language) data.locale = language;
    if (country) data.country = country;
    if (market) data.market = market;

    return data;
  }

  async _parse(response) {
    try {
      const type = response.headers.get('content-type') || '';
      return type.includes('application/json') ? await response.json() : { message: await response.text() };
    } catch {
      return null;
    }
  }

  _reasonSkipsOrder() {
    return !!this.reasonSelect?.selectedOptions[0]?.dataset.skipOrderType;
  }

  _onReasonChange = () => {
    const skipType = this.reasonSelect?.selectedOptions[0]?.dataset.skipOrderType || '';

    if (this.orderField) {
      const container = this.orderField.closest('.input') || this.orderField;
      container.classList.toggle(HIDDEN_CLASS, !!skipType);
      this.orderField.disabled = !!skipType; // excluded from validation + payload
    }

    if (this.advisory) {
      if (skipType === 'order_request') this.advisory.textContent = this._t('missingOrderAdvisory');
      else if (skipType === 'other') this.advisory.textContent = this._t('miscCategoryAdvisory');
      this.advisory.classList.toggle(HIDDEN_CLASS, !skipType);
    }

    if (this.submitButton) {
      const key = SUBMIT_LABELS[this.reasonSelect?.value];
      this.submitButton.textContent = key ? this._t(key) : this.defaultSubmitLabel;
    }
  };

  _applyDeepLinkReason() {
    const slug = new URLSearchParams(location.search).get('reason');
    const code = REASON_SLUGS[(slug || '').trim().toLowerCase()];
    if (!code || ![...this.reasonSelect.options].some((o) => o.value === code)) return;

    this.reasonSelect.value = code;
    
    this.reasonSelect.dispatchEvent(new Event('change', { bubbles: true }));
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  _hasInfluencerParam() {
    return !!new URLSearchParams(location.search).get('influencer')?.trim();
  }

  _applyInfluencerTitle() {
    const title = this.dataset.influencerTitle;
    if (!title) return;

    const param = (new URLSearchParams(location.search).get('influencer') || '').trim().slice(0, INFLUENCER_NAME_MAX_LENGTH);
    if (!param) return;

    const name = param.charAt(0).toLocaleUpperCase() + param.slice(1);

    this.closest('.contact__main')?.querySelector('.section__header')?.classList.add(HIDDEN_CLASS);

    let headline = document.querySelector('.text-block--headline :is(h1, h2)');
    if (!headline) return;

    while (headline.children.length === 1) headline = headline.firstElementChild;
    headline.textContent = title.replace(INFLUENCER_PLACEHOLDER, name);
  }

  // Keeps the order-number field locked to the "#12" prefix and 7 trailing digits.
  _initOrderMask() {
    const field = this.orderField;
    const warning = this.querySelector('[data-order-warning]');
    field.maxLength = ORDER_PREFIX.length + 7; // "#12" + 7 digits

    const setCaret = (pos) => {
      try {
        field.setSelectionRange(pos, pos);
      } catch {
        /* not selectable while hidden */
      }
    };

    const normalize = () => {
      let raw = field.value || '';
      if (!raw.startsWith(ORDER_PREFIX)) raw = ORDER_PREFIX + raw.replace(/^#+/, '').replace(/^12/, '');
      const digits = raw.slice(ORDER_PREFIX.length).replace(/\D/g, '').slice(0, 7);
      field.value = ORDER_PREFIX + digits;
      const caret = Math.max(ORDER_PREFIX.length, Math.min(field.selectionStart || field.value.length, field.value.length));
      requestAnimationFrame(() => setCaret(caret));
    };

    if (field.value.trim()) normalize();
    else field.value = ORDER_PREFIX;

    field.addEventListener('focus', () => {
      if (!field.value.startsWith(ORDER_PREFIX)) field.value = ORDER_PREFIX;
      requestAnimationFrame(() => setCaret(Math.max(ORDER_PREFIX.length, field.value.length)));
    });

    field.addEventListener('keydown', (event) => {
      const start = field.selectionStart || 0;
      // Protect the prefix from Backspace/Delete.
      if ((event.key === 'Backspace' && start <= ORDER_PREFIX.length) || (event.key === 'Delete' && start < ORDER_PREFIX.length)) {
        event.preventDefault();
        requestAnimationFrame(() => setCaret(ORDER_PREFIX.length));
        return;
      }
      // Block non-digit typing after the prefix.
      if (event.key.length === 1 && !/\d/.test(event.key) && !(event.ctrlKey || event.metaKey || event.altKey) && start >= ORDER_PREFIX.length) {
        event.preventDefault();
      }
    });

    field.addEventListener('input', () => {
      normalize();
      warning?.classList.toggle(HIDDEN_CLASS, /^#12\d{0,7}$/.test(field.value));
    });
  }

  _onFilesChange = (event) => {
    const box = this.querySelector('[data-file-names]');
    const files = [...(event.target.files || [])];
    if (!box) return;
    box.innerHTML = files.length
      ? `<strong>${this._escape(this._t('uploadedFiles'))}:</strong><br>${files.map((f) => this._escape(f.name)).join('<br>')}`
      : '';
  };

  _clearFileNames() {
    const box = this.querySelector('[data-file-names]');
    if (box) box.innerHTML = '';
  }

  _setBusy(state) {
    this.submitting = state;
    if (!this.submitButton) return;
    this.submitButton.disabled = state;
    this.submitButton.setAttribute('aria-busy', state ? 'true' : 'false');
  }

  _showBanner(type, messages) {
    if (!this.banner || !this.bannerContent) return;
    const list = [].concat(messages).filter(Boolean);

    this.banner.classList.toggle('banner--success', type === 'success');
    this.banner.classList.toggle('banner--error', type !== 'success');
    this.iconSuccess?.classList.toggle(HIDDEN_CLASS, type !== 'success');
    this.iconError?.classList.toggle(HIDDEN_CLASS, type === 'success');

    if (list.length > 1) {
      this.bannerContent.innerHTML = `<ul>${list.map((m) => `<li>${this._escape(m)}</li>`).join('')}</ul>`;
    } else {
      this.bannerContent.textContent = list[0] || '';
    }

    this.banner.classList.remove(HIDDEN_CLASS);
    this.banner.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  _escape(value) {
    const el = document.createElement('div');
    el.textContent = String(value);
    return el.innerHTML;
  }
}

customElements.define('contact-form', ContactForm);
